import React from 'react';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';
const fmtCurrency = (v, cur = 'EUR') => new Intl.NumberFormat('it-IT', { style: 'currency', currency: cur }).format(v || 0);

const pageBreak = { pageBreakBefore: 'always' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', borderBottom: '3px solid #0f172a', paddingBottom: '16px', marginBottom: '24px' };
const sectionTitle = (text) => ({ margin: '0 0 8px', fontSize: '13px', fontWeight: 700, color: '#0f172a', borderBottom: '2px solid #0f172a', paddingBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' });
const labelStyle = { fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', marginBottom: '2px' };
const valueStyle = { margin: '0 0 0', fontSize: '12px' };
const valueBold = { margin: '0 0 0', fontSize: '12px', fontWeight: 600 };

const EvoHeader = () => (
  <div>
    <img src="/evotech-logo.png" alt="Evotech" style={{ height: '60px', objectFit: 'contain' }} />
  </div>
);

const Footer = () => (
  <div style={{ marginTop: '24px', borderTop: '1px solid #e2e8f0', paddingTop: '8px', textAlign: 'center' }}>
    <p style={{ margin: 0, fontSize: '9px', color: '#94a3b8' }}>Evotech S.r.l. - Documento generato dal sistema ERP</p>
  </div>
);

const SignatureArea = () => null;

const InfoCell = ({ label, children, bold }) => (
  <div style={{ marginBottom: '8px' }}>
    <p style={labelStyle}>{label}</p>
    <p style={bold ? valueBold : valueStyle}>{children || '-'}</p>
  </div>
);

const statusLabel = (s) => {
  const m = { open: 'Aperta', under_analysis: 'In Analisi', waiting: 'In Attesa', closed: 'Chiusa' };
  return m[s] || s;
};

const typeLabel = (t) => {
  const m = { customer: 'Cliente', supplier: 'Fornitore', internal: 'Interna', process: 'Processo' };
  return m[t] || t;
};

const severityLabel = (s) => {
  const m = { minor: 'Minore', major: 'Maggiore', critical: 'Critica' };
  return m[s] || s;
};

const costCatLabel = (c) => {
  const m = { scrap: 'Scarto', rework: 'Rilavorazione', machine_downtime: 'Fermo macchina', transport: 'Trasporto', customer_charge: 'Addebito cliente', sorting: 'Selezione', other: 'Altro' };
  return m[c] || c;
};

// ─── NC Print Section ───

const categoryConfig = {
  interna: { title: 'NON CONFORMITA INTERNA', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  ricevuta: { title: 'NON CONFORMITA RICEVUTA', color: '#0d9488', bg: '#f0fdfa', border: '#99f6e4' },
  emessa: { title: 'NON CONFORMITA EMESSA', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' },
};

const severityConfig = {
  minor: { label: 'Minore', color: '#ca8a04', bg: '#fef9c3' },
  major: { label: 'Maggiore', color: '#ea580c', bg: '#fff7ed' },
  critical: { label: 'Critica', color: '#dc2626', bg: '#fef2f2' },
};

const statusConfig = {
  open: { label: 'Aperta', color: '#dc2626', bg: '#fef2f2' },
  under_analysis: { label: 'In Analisi', color: '#ca8a04', bg: '#fef9c3' },
  waiting: { label: 'In Attesa', color: '#2563eb', bg: '#eff6ff' },
  closed: { label: 'Chiusa', color: '#16a34a', bg: '#f0fdf4' },
};

const StatusPill = ({ value, config }) => {
  const c = config[value] || { label: value, color: '#64748b', bg: '#f1f5f9' };
  return (
    <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, color: c.color, background: c.bg, border: `1px solid ${c.color}30`, letterSpacing: '0.3px' }}>
      {c.label}
    </span>
  );
};

const PrintTable = ({ headers, children }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
    <thead>
      <tr style={{ background: '#f8fafc' }}>
        {headers.map((h, i) => (
          <th key={i} style={{ padding: '7px 12px', textAlign: h.align || 'left', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', borderBottom: '2px solid #cbd5e1', fontWeight: 600 }}>{h.label}</th>
        ))}
      </tr>
    </thead>
    <tbody>{children}</tbody>
  </table>
);

export const PrintableNC = React.forwardRef(({ nc, costs, attachments }, ref) => {
  if (!nc) return null;
  const totalCost = (costs || []).reduce((s, c) => s + (c.amount || 0), 0);
  const imageAttachments = (attachments || []).filter(a => a.file_type && a.file_type.startsWith('image/'));
  const catCfg = categoryConfig[nc.nc_category] || categoryConfig.interna;

  return (
    <div ref={ref} style={{ padding: '36px 40px', fontFamily: 'Arial, sans-serif', color: '#1e293b', fontSize: '12px' }}>

      {/* ═══ Header ═══ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #0f172a', paddingBottom: '14px', marginBottom: '20px' }}>
        <EvoHeader />
        <div style={{ textAlign: 'right' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: catCfg.color, textTransform: 'uppercase', letterSpacing: '1px' }}>{catCfg.title}</h2>
          <p style={{ margin: '6px 0 0', fontFamily: 'monospace', fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>{nc.nc_number}</p>
          <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#64748b' }}>Data apertura: {fmtDate(nc.opening_date)}</p>
        </div>
      </div>

      {/* ═══ Status Banner ═══ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '20px', padding: '12px 20px', background: catCfg.bg, border: `1px solid ${catCfg.border}`, borderRadius: '8px' }}>
        <div style={{ marginRight: '8px' }}>
          <p style={labelStyle}>Tipo</p>
          <p style={valueBold}>{typeLabel(nc.nc_type)}</p>
        </div>
        <div>
          <p style={labelStyle}>Gravita</p>
          <StatusPill value={nc.severity} config={severityConfig} />
        </div>
        <div>
          <p style={labelStyle}>Stato</p>
          <StatusPill value={nc.status} config={statusConfig} />
        </div>
        {nc.macro_area && <div><p style={labelStyle}>Area</p><p style={valueBold}>{nc.macro_area}</p></div>}
        {nc.nc_category === 'emessa' && nc.recipient_type && (
          <div><p style={labelStyle}>Destinatario</p><p style={valueBold}>{nc.recipient_type === 'customer' ? 'Cliente' : 'Fornitore'}</p></div>
        )}
      </div>

      {/* ═══ Riferimenti ═══ */}
      <SectionCard title="Riferimenti" borderColor="#334155">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px 24px' }}>
          <InfoCell label="Prodotto" bold>{nc.product_code ? `${nc.product_code} - ${nc.product_name || ''}` : '-'}</InfoCell>
          <InfoCell label="Lotto" bold>{nc.lot_code || '-'}</InfoCell>
          <InfoCell label="Quantita Coinvolta" bold>{nc.involved_quantity || '-'}</InfoCell>
          <InfoCell label="Cliente">{nc.client_name || '-'}</InfoCell>
          <InfoCell label="Fornitore">{nc.supplier_name || '-'}</InfoCell>
          <InfoCell label="Origine">{nc.origin || '-'}</InfoCell>
          {nc.nc_category === 'ricevuta' && <InfoCell label="Rif. NC Cliente" bold>{nc.external_reference || '-'}</InfoCell>}
          {nc.nc_category === 'ricevuta' && <InfoCell label="Mittente">{nc.sender || '-'}</InfoCell>}
          <InfoCell label="Responsabile" bold>{nc.responsible_person || '-'}</InfoCell>
          <InfoCell label="Scadenza">{fmtDate(nc.due_date)}</InfoCell>
        </div>
      </SectionCard>

      {/* ═══ Descrizione Difetto ═══ */}
      <SectionCard title="Descrizione Difetto / Problema" borderColor="#b91c1c">
        <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.6' }}>{nc.defect_description || '-'}</p>
      </SectionCard>

      {/* ═══ Evidenza Fotografica ═══ */}
      {imageAttachments.length > 0 && (
        <SectionCard title="Evidenza Fotografica" borderColor="#6366f1">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {imageAttachments.map((a, i) => (
              <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden', background: '#fafafa' }}>
                <img
                  src={a.file_url?.startsWith('/') ? `${window.location.origin}/api${a.file_url}` : a.file_url}
                  alt={a.description || a.file_name}
                  style={{ maxWidth: '260px', maxHeight: '190px', objectFit: 'contain', display: 'block', padding: '4px' }}
                  crossOrigin="anonymous"
                />
                <p style={{ margin: 0, padding: '5px 10px', fontSize: '10px', color: '#475569', background: '#f1f5f9', borderTop: '1px solid #e2e8f0' }}>
                  {a.description || a.file_name}
                </p>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ═══ Azioni ═══ */}
      <SectionCard title="Azioni Intraprese" borderColor="#0284c7">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 32px' }}>
          <div>
            <p style={{ ...labelStyle, marginBottom: '3px' }}>Azione di Contenimento</p>
            <p style={{ margin: '0 0 8px', fontSize: '12px', lineHeight: '1.5' }}>{nc.containment_action || 'Non definita'}</p>
          </div>
          <div>
            <p style={{ ...labelStyle, marginBottom: '3px' }}>Azione Correttiva</p>
            <p style={{ margin: '0 0 8px', fontSize: '12px', lineHeight: '1.5' }}>{nc.corrective_action || 'Non definita'}</p>
          </div>
        </div>
      </SectionCard>

      {/* ═══ Disposizione Materiale ═══ */}
      {nc.total_nonconforming_quantity != null && (
        <SectionCard title="Disposizione Materiale" borderColor="#7c3aed">
          <PrintTable headers={[
            { label: 'Totale NC', align: 'center' },
            { label: 'Scarto', align: 'center' },
            { label: 'Scarto Int.', align: 'center' },
            { label: 'Concessione', align: 'center' },
            { label: 'Rilavorati', align: 'center' },
          ]}>
            <tr>
              <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, fontSize: '13px' }}>{nc.total_nonconforming_quantity ?? '-'}</td>
              <td style={{ padding: '8px 12px', textAlign: 'center' }}>{nc.scrapped_quantity ?? '-'}</td>
              <td style={{ padding: '8px 12px', textAlign: 'center' }}>{nc.internal_scrap_quantity ?? '-'}</td>
              <td style={{ padding: '8px 12px', textAlign: 'center' }}>{nc.customer_concession_quantity ?? '-'}</td>
              <td style={{ padding: '8px 12px', textAlign: 'center' }}>{nc.reworked_quantity ?? '-'}</td>
            </tr>
          </PrintTable>
        </SectionCard>
      )}

      {/* ═══ Costi ═══ */}
      {costs && costs.length > 0 && (
        <SectionCard title="Costi Associati" borderColor="#475569">
          <PrintTable headers={[
            { label: 'Categoria' },
            { label: 'Descrizione' },
            { label: 'Importo', align: 'right' },
          ]}>
            {costs.map((c, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '6px 12px' }}>{costCatLabel(c.category)}</td>
                <td style={{ padding: '6px 12px' }}>{c.description || '-'}</td>
                <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600 }}>{fmtCurrency(c.amount)}</td>
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid #0f172a' }}>
              <td colSpan={2} style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700 }}>TOTALE</td>
              <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, fontSize: '14px' }}>{fmtCurrency(totalCost)}</td>
            </tr>
          </PrintTable>
        </SectionCard>
      )}

      {/* ═══ Note ═══ */}
      {nc.notes && (
        <SectionCard title="Note" borderColor="#64748b">
          <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.5' }}>{nc.notes}</p>
        </SectionCard>
      )}

      {/* ═══ Firma ═══ */}
      <div style={{ marginTop: '32px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px' }}>
        <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '8px' }}>
          <p style={{ margin: 0, fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase' }}>Responsabile Qualita</p>
          <p style={{ margin: '24px 0 0', fontSize: '10px', color: '#94a3b8' }}>Data: _______________</p>
        </div>
        <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '8px' }}>
          <p style={{ margin: 0, fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase' }}>Direzione</p>
          <p style={{ margin: '24px 0 0', fontSize: '10px', color: '#94a3b8' }}>Data: _______________</p>
        </div>
      </div>

      <Footer />
    </div>
  );
});
PrintableNC.displayName = 'PrintableNC';


// ─── Debit Note Print Section ───

export const PrintableDebitNoteSection = React.forwardRef(({ data, withPageBreak }, ref) => {
  if (!data) return null;
  const recipient = data.recipient || {};

  return (
    <div ref={ref} style={{ ...(withPageBreak ? pageBreak : {}), padding: '40px', fontFamily: 'Arial, sans-serif', color: '#1e293b', fontSize: '12px' }}>
      {/* Header */}
      <div style={headerStyle}>
        <EvoHeader />
        <div style={{ textAlign: 'right' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>NOTA DI DEBITO</h2>
          <p style={{ margin: '4px 0 0', fontFamily: 'monospace', fontSize: '16px', fontWeight: 700 }}>{data.debit_note_number}</p>
          <p style={{ margin: '2px 0 0', color: '#64748b' }}>Data: {fmtDate(data.issue_date)}</p>
        </div>
      </div>

      {/* Recipient + NC Ref */}
      <div style={{ display: 'flex', gap: '40px', marginBottom: '24px' }}>
        <div style={{ flex: 1 }}>
          <p style={labelStyle}>{data.recipient_type === 'customer' ? 'Cliente' : 'Fornitore'}</p>
          <p style={{ fontWeight: 700, fontSize: '14px', margin: 0 }}>{recipient.name || data.customer_name || data.supplier_name || '-'}</p>
          {recipient.address && <p style={{ margin: '2px 0 0' }}>{recipient.address}</p>}
          {(recipient.zip || recipient.city) && <p style={{ margin: '2px 0 0' }}>{[recipient.zip, recipient.city, recipient.province].filter(Boolean).join(' ')}</p>}
          {recipient.vat && <p style={{ margin: '2px 0 0' }}>P.IVA: {recipient.vat}</p>}
        </div>
        <div style={{ flex: 1 }}>
          <p style={labelStyle}>Riferimento NC</p>
          <p style={{ fontWeight: 700, fontSize: '14px', margin: 0, fontFamily: 'monospace' }}>{data.nc_number || '-'}</p>
          {data.product_code && <p style={{ margin: '4px 0 0' }}>Prodotto: <strong>{data.product_code}</strong> - {data.product_name}</p>}
          {data.lot_code && <p style={{ margin: '2px 0 0' }}>Lotto: <strong>{data.lot_code}</strong></p>}
        </div>
      </div>

      {/* Subject + Description */}
      <div style={{ marginBottom: '16px' }}>
        <p style={labelStyle}>Oggetto</p>
        <p style={{ fontWeight: 600, fontSize: '13px', margin: 0 }}>{data.subject}</p>
      </div>
      {data.description && (
        <div style={{ marginBottom: '16px' }}>
          <p style={labelStyle}>Descrizione</p>
          <p style={{ margin: 0 }}>{data.description}</p>
        </div>
      )}

      {/* Cost Lines */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead>
          <tr style={{ background: '#f1f5f9' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '10px', textTransform: 'uppercase', color: '#64748b', borderBottom: '2px solid #0f172a' }}>Descrizione Voce</th>
            <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: '10px', textTransform: 'uppercase', color: '#64748b', borderBottom: '2px solid #0f172a', width: '80px' }}>Qtà</th>
            <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '10px', textTransform: 'uppercase', color: '#64748b', borderBottom: '2px solid #0f172a', width: '110px' }}>Costo Unit.</th>
            <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '10px', textTransform: 'uppercase', color: '#64748b', borderBottom: '2px solid #0f172a', width: '110px' }}>Totale</th>
          </tr>
        </thead>
        <tbody>
          {(data.lines || []).map((line, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
              <td style={{ padding: '8px 12px' }}>{line.description}</td>
              <td style={{ padding: '8px 12px', textAlign: 'center' }}>{line.quantity}</td>
              <td style={{ padding: '8px 12px', textAlign: 'right' }}>{fmtCurrency(line.unit_cost, data.currency)}</td>
              <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{fmtCurrency(line.total_cost, data.currency)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #0f172a' }}>
            <td colSpan={3} style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: '13px' }}>TOTALE</td>
            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 800, fontSize: '15px' }}>{fmtCurrency(data.total_amount, data.currency)}</td>
          </tr>
        </tfoot>
      </table>

      {/* Notes */}
      {data.notes && (
        <div style={{ marginBottom: '16px' }}>
          <p style={labelStyle}>Note</p>
          <p style={{ margin: 0 }}>{data.notes}</p>
        </div>
      )}

      <SignatureArea />
      <Footer />
    </div>
  );
});
PrintableDebitNoteSection.displayName = 'PrintableDebitNoteSection';


// ─── Combined Dossier ───

export const PrintableDossier = React.forwardRef(({ nc, costs, attachments, debitNote }, ref) => {
  return (
    <div ref={ref}>
      <PrintableNC nc={nc} costs={costs} attachments={attachments} />
      {debitNote && <PrintableDebitNoteSection data={debitNote} withPageBreak />}
    </div>
  );
});
PrintableDossier.displayName = 'PrintableDossier';


// ─── NCR Response Document (Risposta a NC Ricevuta) ───

const technicalOutcomeLabel = (v) => ({ accettata: 'ACCETTATA', parzialmente_accettata: 'PARZIALMENTE ACCETTATA', rifiutata: 'RIFIUTATA' }[v] || v || '-');
const economicOutcomeLabel = (v) => ({ nessun_addebito: 'Nessun addebito', addebito_accettato: 'Addebito accettato', addebito_parzialmente_accettato: 'Addebito parzialmente accettato', addebito_rifiutato: 'Addebito rifiutato' }[v] || v || '-');

const OutcomeBanner = ({ outcome }) => {
  const config = {
    accettata:               { bg: '#dcfce7', border: '#16a34a', color: '#15803d', label: 'NC ACCETTATA' },
    parzialmente_accettata:  { bg: '#fef9c3', border: '#ca8a04', color: '#a16207', label: 'NC PARZIALMENTE ACCETTATA' },
    rifiutata:               { bg: '#fef2f2', border: '#dc2626', color: '#b91c1c', label: 'NC RIFIUTATA' },
  };
  const c = config[outcome] || config.rifiutata;
  return (
    <div style={{ margin: '0 0 24px', padding: '16px 24px', background: c.bg, border: `3px solid ${c.border}`, borderRadius: '8px', textAlign: 'center' }}>
      <p style={{ margin: 0, fontSize: '22px', fontWeight: 900, color: c.color, letterSpacing: '2px' }}>{c.label}</p>
    </div>
  );
};

const SectionCard = ({ title, borderColor, children }) => (
  <div style={{ marginBottom: '18px', border: `1px solid ${borderColor || '#e2e8f0'}`, borderRadius: '6px', overflow: 'hidden' }}>
    <div style={{ padding: '8px 16px', background: borderColor ? `${borderColor}15` : '#f8fafc', borderBottom: `2px solid ${borderColor || '#e2e8f0'}` }}>
      <h3 style={{ margin: 0, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: borderColor || '#475569' }}>{title}</h3>
    </div>
    <div style={{ padding: '12px 16px' }}>{children}</div>
  </div>
);

export const PrintableNCRResponse = React.forwardRef(({ nc, attachments }, ref) => {
  if (!nc) return null;

  const outcomeColor = nc.technical_outcome === 'accettata' ? '#16a34a' : nc.technical_outcome === 'rifiutata' ? '#dc2626' : '#d97706';
  const imageAttachments = (attachments || []).filter(a => a.file_type && a.file_type.startsWith('image/'));

  return (
    <div ref={ref} style={{ padding: '36px 40px', fontFamily: 'Arial, sans-serif', color: '#1e293b', fontSize: '12px' }}>

      {/* ═══ Header ═══ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #0f172a', paddingBottom: '14px', marginBottom: '20px' }}>
        <EvoHeader />
        <div style={{ textAlign: 'right' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '1px' }}>Risposta a Non Conformita</h2>
          <p style={{ margin: '6px 0 0', fontFamily: 'monospace', fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>{nc.nc_number}</p>
          <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#64748b' }}>Data risposta: {fmtDate(nc.response_date)} &nbsp;|&nbsp; Apertura: {fmtDate(nc.opening_date)}</p>
        </div>
      </div>

      {/* ═══ Outcome Banner ═══ */}
      {nc.technical_outcome && <OutcomeBanner outcome={nc.technical_outcome} />}

      {/* ═══ Riferimenti ═══ */}
      <SectionCard title="Riferimenti" borderColor="#334155">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px 24px' }}>
          <InfoCell label="Rif. NC Cliente" bold>{nc.external_reference || '-'}</InfoCell>
          <InfoCell label="Cliente" bold>{nc.client_name || '-'}</InfoCell>
          <InfoCell label="Mittente">{nc.sender || '-'}</InfoCell>
          <InfoCell label="Prodotto" bold>{nc.product_code ? `${nc.product_code} - ${nc.product_name || ''}` : '-'}</InfoCell>
          <InfoCell label="Lotto" bold>{nc.lot_code || '-'}</InfoCell>
          <InfoCell label="Quantita Coinvolta" bold>{nc.involved_quantity || '-'}</InfoCell>
          <InfoCell label="Data Ricezione">{fmtDate(nc.received_date)}</InfoCell>
          <InfoCell label="Deadline Risposta">{fmtDate(nc.response_deadline)}</InfoCell>
          <InfoCell label="Gravita" bold>{severityLabel(nc.severity)}</InfoCell>
        </div>
      </SectionCard>

      {/* ═══ Descrizione Difetto / Reclamo ═══ */}
      <SectionCard title="Descrizione Difetto / Reclamo del Cliente" borderColor="#b91c1c">
        <p style={{ margin: '0 0 6px', fontSize: '12px', lineHeight: '1.6' }}>{nc.defect_description || '-'}</p>
        {nc.complaint_description && (
          <div style={{ marginTop: '10px', padding: '10px 14px', background: '#fef2f2', borderLeft: '4px solid #dc2626', borderRadius: '0 4px 4px 0' }}>
            <p style={{ ...labelStyle, marginBottom: '4px', color: '#b91c1c' }}>Descrizione Reclamo Cliente</p>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.6' }}>{nc.complaint_description}</p>
          </div>
        )}
      </SectionCard>

      {/* ═══ Evidenza Fotografica ═══ */}
      {imageAttachments.length > 0 && (
        <SectionCard title="Evidenza Fotografica" borderColor="#6366f1">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {imageAttachments.map((a, i) => (
              <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden', background: '#fafafa' }}>
                <img
                  src={`${window.location.origin}/api${a.file_url}`}
                  alt={a.description || a.file_name}
                  style={{ maxWidth: '260px', maxHeight: '190px', objectFit: 'contain', display: 'block', padding: '4px' }}
                  crossOrigin="anonymous"
                />
                <p style={{ margin: 0, padding: '5px 10px', fontSize: '10px', color: '#475569', background: '#f1f5f9', borderTop: '1px solid #e2e8f0' }}>
                  {a.description || a.file_name}
                </p>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ═══ Analisi e Azioni ═══ */}
      <SectionCard title="Analisi e Azioni Intraprese" borderColor="#0284c7">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 32px' }}>
          <div>
            <p style={{ ...labelStyle, marginBottom: '3px' }}>Azione di Contenimento</p>
            <p style={{ margin: '0 0 8px', fontSize: '12px', lineHeight: '1.5' }}>{nc.containment_action || 'Non definita'}</p>
          </div>
          <div>
            <p style={{ ...labelStyle, marginBottom: '3px' }}>Azione Correttiva</p>
            <p style={{ margin: '0 0 8px', fontSize: '12px', lineHeight: '1.5' }}>{nc.corrective_action || 'Non definita'}</p>
          </div>
        </div>
      </SectionCard>

      {/* ═══ Esito Contestazione ═══ */}
      <SectionCard title="Esito Contestazione" borderColor={outcomeColor}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
          <div>
            <p style={labelStyle}>Esito Tecnico</p>
            <p style={{ margin: 0, fontSize: '15px', fontWeight: 900, color: outcomeColor }}>{technicalOutcomeLabel(nc.technical_outcome)}</p>
          </div>
          <div>
            <p style={labelStyle}>Data Risposta</p>
            <p style={valueBold}>{fmtDate(nc.response_date)}</p>
          </div>
        </div>
        {nc.outcome_reason && (
          <div style={{ marginTop: '10px', padding: '10px 14px', background: '#f8fafc', borderLeft: `4px solid ${outcomeColor}`, borderRadius: '0 4px 4px 0' }}>
            <p style={{ ...labelStyle, marginBottom: '3px' }}>Motivazione</p>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.6' }}>{nc.outcome_reason}</p>
          </div>
        )}
        {nc.response_notes && (
          <div style={{ marginTop: '8px' }}>
            <p style={{ ...labelStyle, marginBottom: '3px' }}>Note Risposta</p>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.5' }}>{nc.response_notes}</p>
          </div>
        )}
      </SectionCard>

      {/* ═══ Gestione Economica ═══ */}
      {(nc.economic_outcome || nc.requested_charge_amount != null || nc.recognized_charge_amount != null) && (
        <SectionCard title="Gestione Economica" borderColor="#475569">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px 16px' }}>
            <div>
              <p style={labelStyle}>Addebito Richiesto</p>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#dc2626' }}>{nc.requested_charge_amount != null ? fmtCurrency(nc.requested_charge_amount) : '-'}</p>
            </div>
            <div>
              <p style={labelStyle}>Addebito Riconosciuto</p>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#16a34a' }}>{nc.recognized_charge_amount != null ? fmtCurrency(nc.recognized_charge_amount) : '-'}</p>
            </div>
            <div>
              <p style={labelStyle}>Differenza</p>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: 700 }}>{nc.requested_charge_amount != null && nc.recognized_charge_amount != null ? fmtCurrency(nc.requested_charge_amount - nc.recognized_charge_amount) : '-'}</p>
            </div>
            <div>
              <p style={labelStyle}>Esito Economico</p>
              <p style={valueBold}>{economicOutcomeLabel(nc.economic_outcome)}</p>
            </div>
          </div>
          {nc.economic_notes && (
            <div style={{ marginTop: '8px' }}>
              <p style={{ ...labelStyle, marginBottom: '3px' }}>Note Economiche</p>
              <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.5' }}>{nc.economic_notes}</p>
            </div>
          )}
        </SectionCard>
      )}

      {/* ═══ Note Generali ═══ */}
      {nc.notes && (
        <div style={{ marginBottom: '16px' }}>
          <h3 style={sectionTitle()}>Note Generali</h3>
          <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.5' }}>{nc.notes}</p>
        </div>
      )}

      {/* ═══ Firma ═══ */}
      <div style={{ marginTop: '32px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px' }}>
        <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '8px' }}>
          <p style={{ margin: 0, fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase' }}>Responsabile Qualita</p>
          <p style={{ margin: '24px 0 0', fontSize: '10px', color: '#94a3b8' }}>Data: _______________</p>
        </div>
        <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '8px' }}>
          <p style={{ margin: 0, fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase' }}>Direzione</p>
          <p style={{ margin: '24px 0 0', fontSize: '10px', color: '#94a3b8' }}>Data: _______________</p>
        </div>
      </div>

      <Footer />
    </div>
  );
});
PrintableNCRResponse.displayName = 'PrintableNCRResponse';
